import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ccConfig } from '../CC/CC';
import {  useCCContext } from '../CC/CCContext';
import GenericCheckbox from '../GenericComponents/GenericCheckbox';
import GenericIconButton from '../GenericComponents/GenericIconButton';
//import GenericModal from '../GenericComponents/GenericModal';
import * as ccUtils from '../CC/utils';
import styles from './MobileSearch.module.css';
import MobileQuerySummary from './MobileQuerySummary';
//import Count from '../Navigator/Count';
import MobileBrowseValues from './MobileBrowseValues';
import MobileScrollResults from './MobileScrollResults';
//import MobileMap from './MobileMap';
//import Icons from '../Result/Icons';
import GenericIcon from '../GenericComponents/GenericIcon';

/*
interface SearchValue {
    source: string;
    text: string;
}
*/
type SearchProps = {
    mainY: number;
    setAndKeepMainY: (newMainY: number, prevMainY: number) => void;
    selectedRecord: any | null;
    setSelectedRecord: React.Dispatch<React.SetStateAction<any | null>>;
};

const MobileSearch: React.FC<SearchProps> = ({ mainY, setAndKeepMainY, selectedRecord, setSelectedRecord }) => {
    const { t } = useTranslation();
    const { cc, setCc, setViewMode } = useCCContext();
    const ctx = useCCContext();
    const [selectedField, setSelectedField] = useState<string>(Object.keys(ccConfig.mobileSearchFields)[0]);
    const [searchText, setSearchText] = useState<string>("");
    //const [showMap, setShowMap] = useState<boolean>(false);
    const [showBrowseValues, setShowBrowseValues] = useState(false);
    const [showScrollToTop, setShowScrollToTop] = useState(false); // State to manage visibility of scroll-to-top button
    const topRef = useRef<HTMLDivElement | null>(null);
    const pageRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    setViewMode("icons");

    useEffect(() => {
        const handleScroll = () => {
            if (pageRef?.current?.scrollTop && pageRef?.current?.scrollTop > 200) { // Show button after scrolling down 200px
                setShowScrollToTop(true);
            } else {
                setShowScrollToTop(false);
            }
        };

        const currentRef = pageRef.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, []);

    const showHomepage = () => {
        setAndKeepMainY(-100, -200);
    }

    const showMenu = () => {
        setAndKeepMainY(-400, -200);
    }

    const handleValueSelection = (selectedValue: string) => {
        if (topRef.current) {
            topRef.current.scrollIntoView();
        }
        setSearchText("");
        setShowBrowseValues(!showBrowseValues);

        let encodedValue = selectedValue.replace(/\[/g, encodeURIComponent('[')).replace(/\]/g, encodeURIComponent(']'));

        setTimeout(() => {
            // Check if search criteria already exist for the selected field
            const existingCriterion = cc.customSearchCriteria.find(criterion => criterion.field === ccConfig.mobileSearchFields[selectedField].field);

            if (existingCriterion) {
                // If they exist, add the new value to the existing criteria
                const newValues = [...existingCriterion.searchValues, `[${encodedValue}]`];
                // Ensure the new values are unique
                const uniqueValues = Array.from(new Set(newValues));

                const updatedCriterion = {
                    ...existingCriterion,
                    searchValues: uniqueValues
                };

                setCc(prevCc => ({
                    ...prevCc,
                    customSearchCriteria: prevCc.customSearchCriteria.map(criterion =>
                        criterion.field === existingCriterion.field ? updatedCriterion : criterion
                    )
                }));
            } else {
                // If they do not exist, add the new criteria
                ccUtils.addCustomSearch(ctx, ccConfig.mobileSearchFields[selectedField].field, selectedField, `[${encodedValue}]`);
            }
        }, 10);
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            executeSearch();
        }
    };

    const executeSearch = () => {
        if (searchText.trim() !== "") {
            ccUtils.addCustomSearch(ctx, ccConfig.mobileSearchFields[selectedField].field, selectedField, searchText);
        }
        setSearchText("");
        setShowBrowseValues(false);
    };

    const handleMatchAllChange = (checked: boolean, value: string) => {
        //setMatchAll(checked);
        setCc({
            ...cc,
            matchAllCriteria: checked,
        });
    };

    const shouldExcludeCollection = () => {
        const query = ccUtils.generateQuery(cc, ctx.user);
        //console.log(query, !(query.indexOf('thème: Tapisseries') > 0 || query.indexOf('theme: Tapisseries') > 0));
        return !(query.indexOf('thème: Tapisseries') > 0 || query.indexOf('theme: Tapisseries') > 0);
    };

    return (
        <div ref={pageRef} className={styles.MobileSearch}>
            <div ref={topRef} className={styles.header}>
                <div className={styles.header_logo}
                    onClick={(e: any) => {
                        e.preventDefault();
                        setViewMode('homepage');
                    }}>
                    <div>
                        <div className={styles.header_logo_svg}>
<svg aria-label="Back to home (Arts et Métiers.net)" id="logo_muam" data-name="logo muam" xmlns="http://www.w3.org/2000/svg" width="134.914" height="45.247" viewBox="0 0 134.914 45.247">
  <defs>
    <clipPath id="clip-path">
      <rect id="Rectangle_107" data-name="Rectangle 107" width="134.914" height="6.126" fill="#f76f62"></rect>
    </clipPath>
  </defs>
  <path id="Tracé_78" data-name="Tracé 78" d="M292.841,0a11.128,11.128,0,0,0-10.393,7.068,11.173,11.173,0,0,0-6.234-1.888A11.045,11.045,0,0,0,265.1,16.126V28.2H270.7V16.126a5.512,5.512,0,0,1,11.022,0V28.2h5.605V10.946a5.512,5.512,0,0,1,11.022,0V28.2h5.605V10.946A11.045,11.045,0,0,0,292.841,0" transform="translate(-169.044)" fill="#f77061"></path>
  <path id="Tracé_79" data-name="Tracé 79" d="M27.885,5.181a11.173,11.173,0,0,0-6.234,1.888A11.141,11.141,0,0,0,.142,10.947V28.2H5.747V10.947a5.512,5.512,0,0,1,11.022,0V28.2h5.605V16.125a5.512,5.512,0,0,1,11.022,0V28.2H39V16.126A11.045,11.045,0,0,0,27.885,5.181" transform="translate(-0.142 0)" fill="#f77061"></path>
  <path id="Tracé_80" data-name="Tracé 80" d="M134.965,36.142a6.443,6.443,0,0,1-12.885,0V29.757h-5.605v6.385a12.049,12.049,0,0,0,24.1,0V29.757h-5.605Z" transform="translate(-74.301 -19.134)" fill="#f77061"></path>
  <path id="Tracé_81" data-name="Tracé 81" d="M213.481,25.467v-.015h0a11.129,11.129,0,0,0-22.255.016h5.605a5.523,5.523,0,0,1,11.044,0v2.339a10.778,10.778,0,0,0-5.528-1.545A11.35,11.35,0,0,0,191.22,37.533h5.605a5.527,5.527,0,1,1,11.044,0h5.611V25.468h0" transform="translate(-121.949 -9.331)" fill="#f77061"></path>
  <g id="Groupe_63" data-name="Groupe 63" transform="translate(0 39.121)">
    <g id="Groupe_62" data-name="Groupe 62" transform="translate(0 0)" clip-path="url(#clip-path)">
      <path id="Tracé_102" data-name="Tracé 102" d="M129.192,3.722a1.118,1.118,0,0,1,1.216-1.146c.638,0,.884.414.884.965V6.022h.761v-2.3a1.109,1.109,0,0,1,1.216-1.146c.638,0,.884.414.884.965V6.022h.761V3.36c0-.879-.429-1.465-1.339-1.465A1.446,1.446,0,0,0,132.088,3.1h-.079A1.191,1.191,0,0,0,130.732,1.9a1.493,1.493,0,0,0-1.478,1.146h-.079V2h-.744V6.022h.761Zm-2.52,2.3h.726V3.446c0-1-.5-1.542-1.636-1.542a1.578,1.578,0,0,0-1.732,1.25l.717.172a.922.922,0,0,1,.989-.8c.665,0,.9.327.9.93v.327h-1.313c-1,0-1.453.509-1.453,1.206,0,.776.5,1.12,1.269,1.12a1.419,1.419,0,0,0,1.452-1.042h.079ZM125.4,5.506c-.6,0-.8-.284-.8-.6,0-.4.306-.6.84-.6h1.2v.224c0,.569-.482.982-1.234.982m-5.18-1.783a1.119,1.119,0,0,1,1.216-1.146c.656,0,.884.414.884.965V6.022h.761V3.36c0-.879-.411-1.465-1.321-1.465a1.517,1.517,0,0,0-1.479,1.146h-.079V2h-.744V6.022h.761Zm-3.972,2.4a2.2,2.2,0,0,0,2.4-1.99L117.888,4a1.528,1.528,0,0,1-1.636,1.439c-1.155,0-1.688-.775-1.688-2.008,0-1.172.534-1.982,1.706-1.982a1.513,1.513,0,0,1,1.593,1.4l.761-.146A2.17,2.17,0,0,0,116.278.749a2.408,2.408,0,0,0-2.52,2.68,2.39,2.39,0,0,0,2.493,2.7m-6.579-.009a1.614,1.614,0,0,0,1.784-1.249l-.665-.181a1.012,1.012,0,0,1-1.119.81,1.2,1.2,0,0,1-1.286-1.267h3.079c.018-.181.027-.362.027-.482A1.683,1.683,0,0,0,109.655,1.9a1.938,1.938,0,0,0-2.03,2.146,1.885,1.885,0,0,0,2.048,2.076m-.035-3.627c.753,0,1.112.431,1.147,1.2h-2.389a1.168,1.168,0,0,1,1.242-1.2M104.851.853h-.805V6.022H107.3V5.307h-2.45ZM92.264,6.126c1.138,0,1.619-.482,1.619-1.2,0-.646-.394-1.025-1.566-1.232-.8-.129-1.015-.353-1.015-.672,0-.354.271-.569.814-.569a.854.854,0,0,1,.954.767l.7-.147A1.42,1.42,0,0,0,92.133,1.9c-1.041,0-1.567.465-1.567,1.172,0,.637.394,1.008,1.452,1.2.858.147,1.129.31,1.129.689s-.3.6-.875.6a1.089,1.089,0,0,1-1.208-.9l-.691.138c.166.8.612,1.327,1.89,1.327m-3.666-.1V4.032c0-.9.359-1.249,1.242-1.249h.21V1.956h-.157c-.63,0-1.05.31-1.26,1.189l-.062-.009V2h-.735V6.022Zm-3.4.1a1.614,1.614,0,0,0,1.785-1.249l-.665-.181a1.013,1.013,0,0,1-1.12.81A1.2,1.2,0,0,1,83.908,4.23h3.08c.017-.181.026-.362.026-.482A1.683,1.683,0,0,0,85.177,1.9a1.938,1.938,0,0,0-2.03,2.146,1.885,1.885,0,0,0,2.048,2.076M85.16,2.49c.753,0,1.111.431,1.146,1.2H83.917a1.167,1.167,0,0,1,1.242-1.2M82.246,2h-.761V6.022h.761ZM82.307.543h-.875v.68h.875ZM78.58,4.825a1.155,1.155,0,0,0,1.234,1.3,2.617,2.617,0,0,0,.875-.138l-.1-.664a2.226,2.226,0,0,1-.621.1c-.455,0-.63-.232-.63-.689V2.619h1.3V2h-1.3V.87H78.58V2h-.621v.62h.621Zm-3.6-3.292L77.1.551,76.8,0,74.783,1.163Zm.709,4.584a1.614,1.614,0,0,0,1.785-1.249L76.8,4.687a1.013,1.013,0,0,1-1.12.81A1.2,1.2,0,0,1,74.4,4.23h3.08c.017-.181.026-.362.026-.482A1.683,1.683,0,0,0,75.666,1.9a1.938,1.938,0,0,0-2.03,2.146,1.885,1.885,0,0,0,2.048,2.076M75.649,2.49c.753,0,1.111.431,1.146,1.2H74.406a1.167,1.167,0,0,1,1.243-1.2m-7.918.974c0-.6.009-1.362-.009-1.956h.148c.184.612.429,1.344.7,2.068l.857,2.137h.814l.875-2.1c.271-.68.533-1.473.726-2.1h.166c-.018.586-.036,1.37-.036,1.956V6.022h.8V.853H71.511l-.963,2.378c-.149.4-.385,1.094-.568,1.671h-.166c-.175-.577-.429-1.3-.56-1.654l-.936-2.4H66.978V6.022h.753ZM62.4,4.825a1.155,1.155,0,0,0,1.234,1.3,2.617,2.617,0,0,0,.875-.138l-.1-.664a2.229,2.229,0,0,1-.621.1c-.455,0-.63-.232-.63-.689V2.619h1.3V2h-1.3V.87H62.4V2h-.621v.62H62.4Zm-2.9,1.292a1.615,1.615,0,0,0,1.785-1.249l-.665-.181a1.013,1.013,0,0,1-1.12.81A1.2,1.2,0,0,1,58.22,4.23H61.3c.018-.181.027-.362.027-.482A1.683,1.683,0,0,0,59.488,1.9a1.938,1.938,0,0,0-2.03,2.146,1.885,1.885,0,0,0,2.048,2.076M59.471,2.49c.752,0,1.111.431,1.146,1.2H58.228a1.168,1.168,0,0,1,1.243-1.2M53.6,6.126c1.137,0,1.619-.482,1.619-1.2,0-.646-.394-1.025-1.567-1.232-.8-.129-1.015-.353-1.015-.672,0-.354.271-.569.813-.569a.854.854,0,0,1,.954.767l.7-.147A1.42,1.42,0,0,0,53.469,1.9c-1.041,0-1.567.465-1.567,1.172,0,.637.394,1.008,1.452,1.2.858.147,1.129.31,1.129.689s-.3.6-.875.6a1.089,1.089,0,0,1-1.208-.9L51.71,4.8c.166.8.612,1.327,1.89,1.327m-4.41-1.3a1.155,1.155,0,0,0,1.234,1.3,2.617,2.617,0,0,0,.875-.138l-.1-.664a2.232,2.232,0,0,1-.621.1c-.455,0-.63-.232-.63-.689V2.619h1.3V2h-1.3V.87H49.19V2h-.621v.62h.621ZM46.8,6.022V4.032c0-.9.358-1.249,1.242-1.249h.21V1.956H48.1c-.63,0-1.05.31-1.26,1.189l-.061-.009V2H46.04V6.022ZM42.217,3.334c.219-.577.447-1.232.621-1.783h.219c.166.543.394,1.206.612,1.783l.237.638H41.981Zm2.45,2.688h.858L43.495.853H42.427L40.388,6.022h.84l.534-1.447h2.371Zm-7.839.1c1.137,0,1.619-.482,1.619-1.2,0-.646-.394-1.025-1.566-1.232-.805-.129-1.015-.353-1.015-.672,0-.354.271-.569.814-.569a.853.853,0,0,1,.954.767l.7-.147A1.421,1.421,0,0,0,36.7,1.9c-1.041,0-1.566.465-1.566,1.172,0,.637.393,1.008,1.452,1.2.857.147,1.129.31,1.129.689s-.3.6-.875.6a1.088,1.088,0,0,1-1.208-.9l-.692.138c.166.8.613,1.327,1.89,1.327M32.6,6.117a1.614,1.614,0,0,0,1.785-1.249l-.665-.181a1.013,1.013,0,0,1-1.12.81A1.2,1.2,0,0,1,31.315,4.23H34.4c.017-.181.026-.362.026-.482A1.683,1.683,0,0,0,32.583,1.9a1.938,1.938,0,0,0-2.03,2.146A1.885,1.885,0,0,0,32.6,6.117M32.566,2.49c.753,0,1.111.431,1.146,1.2H31.324a1.168,1.168,0,0,1,1.242-1.2M28.979,6.022h.761V.491h-.761V3.067h-.052A1.45,1.45,0,0,0,27.465,1.9c-1,0-1.584.758-1.584,2.128s.569,2.1,1.566,2.1a1.5,1.5,0,0,0,1.479-1.18h.052Zm-1.2-.568c-.779,0-1.146-.465-1.146-1.43,0-.982.376-1.447,1.146-1.447a1.175,1.175,0,0,1,1.2,1.292v.31a1.157,1.157,0,0,1-1.2,1.275M21.8,6.117a1.615,1.615,0,0,0,1.785-1.249l-.665-.181a1.013,1.013,0,0,1-1.12.81A1.2,1.2,0,0,1,20.509,4.23h3.08c.017-.181.026-.362.026-.482A1.683,1.683,0,0,0,21.778,1.9a1.938,1.938,0,0,0-2.03,2.146A1.885,1.885,0,0,0,21.8,6.117M21.761,2.49c.753,0,1.111.431,1.146,1.2H20.518a1.168,1.168,0,0,1,1.242-1.2m-5.11-.956L18.777.551,18.479,0,16.458,1.163Zm.709,4.584a1.614,1.614,0,0,0,1.785-1.249l-.665-.181a1.013,1.013,0,0,1-1.12.81A1.2,1.2,0,0,1,16.073,4.23h3.08c.017-.181.026-.362.026-.482A1.682,1.682,0,0,0,17.342,1.9a1.938,1.938,0,0,0-2.03,2.146,1.884,1.884,0,0,0,2.047,2.076M17.324,2.49c.753,0,1.111.431,1.146,1.2H16.082a1.168,1.168,0,0,1,1.242-1.2M13.151,6.126c1.137,0,1.619-.482,1.619-1.2,0-.646-.394-1.025-1.566-1.232-.805-.129-1.015-.353-1.015-.672,0-.354.271-.569.814-.569a.853.853,0,0,1,.954.767l.7-.147A1.421,1.421,0,0,0,13.02,1.9c-1.041,0-1.566.465-1.566,1.172,0,.637.393,1.008,1.452,1.2.857.147,1.129.31,1.129.689s-.3.6-.875.6a1.088,1.088,0,0,1-1.207-.9l-.692.138c.166.8.613,1.327,1.89,1.327m-3.395-.1H10.5V2H9.739V4.3A1.1,1.1,0,0,1,8.531,5.437c-.656,0-.892-.4-.892-.965V2H6.877V4.653c0,.878.411,1.473,1.33,1.473A1.524,1.524,0,0,0,9.686,4.971h.07Zm-9-2.559c0-.6.009-1.362-.009-1.956H.893c.183.612.428,1.344.7,2.068L2.45,5.712h.814l.875-2.1c.271-.68.533-1.473.726-2.1h.166C5.014,2.094,5,2.878,5,3.464V6.022h.8V.853H4.533L3.57,3.231C3.421,3.627,3.185,4.325,3,4.9H2.835c-.175-.577-.429-1.3-.56-1.654L1.339.853H0V6.022H.753Z" transform="translate(0 0)" fill="#f76f62"></path>
      <path id="Tracé_103" data-name="Tracé 103" d="M423.332,12.966v-.677a.669.669,0,1,0,0-1.335v-.677a1.346,1.346,0,1,1,0,2.689" transform="translate(-325.267 -7.933)" fill="#f76f62"></path>
    </g>
  </g>
</svg>                        </div>
                    </div>
                </div>
                <div className={styles.topRightButton}>
                    <GenericIconButton
                        icon='light/house.svg'
                        hoverEnabled={true}
                        width='28px'
                        height='28px'
                        onClick={(e: any) => {
                            showHomepage()
                        }}
                    />
                </div>
            </div>
            <div className={styles.searchbox}>
                <span style={{ display: 'none' }}>
                    <GenericCheckbox
                        id={'mobileSearchConformToAllCriteria'}
                        name={'mobileSearchConformToAllCriteria'}
                        value={''}
                        checked={cc.matchAllCriteria}
                        onChange={handleMatchAllChange}
                        label={t("mobile_all_criteria")}
                    />
                </span>
                <select
                    value={selectedField}
                    onChange={e => {
                        setSelectedField(e.target.value);
                        // Check if the selected field has browse set to true
                        if (ccConfig.mobileSearchFields[e.target.value].browse) {
                            setShowBrowseValues(true);
                        } else {
                            setShowBrowseValues(false);
                        }
                        if (inputRef.current) {
                            inputRef.current.focus();
                        }
                    }}
                >
                    {Object.keys(ccConfig.mobileSearchFields)
                        .filter(key => !(shouldExcludeCollection() && key === 'Collection'))
                        .map(key => (
                            <option key={key} value={key}>{t(key)}</option>
                        ))}
                </select>

                <div className={styles.searchInputContainer}>
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchText}
                        onChange={e => {
                            setSearchText(e.target.value);
                            if (e.target.value) {
                                setShowBrowseValues(true);
                            }
                        }}
                        onKeyPress={handleKeyPress}
                    />
                    {ccConfig.mobileSearchFields[selectedField].browse ? (
                        <GenericIconButton isActive={showBrowseValues} width='32px' height='32px' icon='light/filter.svg' onClick={() => setShowBrowseValues(!showBrowseValues)} />
                    ) : ("")
                    }
                </div>

                <button onClick={executeSearch}>{t("mobile_search")}</button>

            </div>

            <div className={`${styles.browseValues} ${showBrowseValues ? styles.show : ''}`}>
                {ccConfig.mobileSearchFields[selectedField].browse ? (
                    <MobileBrowseValues field={ccConfig.mobileSearchFields[selectedField].field} searchText={searchText} onValueSelected={handleValueSelection}></MobileBrowseValues>
                ) : ("")
                }
            </div>

            <div className={`${styles.mobileResults} ${showBrowseValues ? '' : styles.show}`}>
                <MobileQuerySummary></MobileQuerySummary>
                <MobileScrollResults mainY={mainY} setAndKeepMainY={setAndKeepMainY} selectedRecord={selectedRecord} setSelectedRecord={setSelectedRecord} />
            </div>

            {/*showMap &&
                <div className={styles.modalMap}>
                    <div className={styles.map}>
                        <div className={styles.closeButton}>
                            <GenericIconButton icon='light/times.svg' width='28px' height='28px' onClick={() => { setShowMap(false) }} />
                        </div>
                        <MobileMap dzi={`${process.env.REACT_APP_IMAGE_BASE_URL}/insert map dzi file here`} />
                    </div>
                </div>
            */}


            <div className={`${styles.scrollToTop} ${showScrollToTop ? styles.show : ''}`} onClick={() => {pageRef?.current?.scrollTo({ top: 0, behavior: 'smooth' })}}>
                <GenericIcon icon='regular/chevron-up.svg'/>
            </div>
        </div>
    );
}

export default MobileSearch;


/*
            <button aria-label='Informations'
                className={styles.infoButton}
                onClick={showMenu}>
                <GenericIcon icon='solid/info.svg' />
            </button>
*/